import React from 'react';
import { Link } from 'react-router-dom';
import Flagship from '../components/Flagship';
import { StatusBadge } from '../components/Ledger';
import { alsoBuilding, flagships } from '../data/projects';
import { RESUME_DOWNLOAD_NAME, RESUME_PATH } from '../data/site';
import usePageMeta from '../hooks/usePageMeta';
import '../styles/Home.css';

const ROLES = ['Technical Product Lead', 'Prompt Engineer', 'Context Engineer', 'Automation Specialist'];

const TICKER = [
  { light: '●', tone: 'live', text: 'Trading platform · In production · SPY / QQQ / IWM 0DTE' },
  { light: '●', tone: 'live', text: 'Strike After Dark · Live · Platform fee on every ticket' },
  { light: '◆', tone: 'muted', text: 'Zoey AI concierge · Private testing' },
  { light: '■', tone: 'muted', text: '57 automated backend tests passing' },
  { light: '■', tone: 'muted', text: 'Built with Cursor · Claude Code · ChatGPT · Perplexity' },
];

const HOW_I_SHIP = [
  { step: '01', role: 'CEO', text: 'I set the vision, make the calls, approve every release.' },
  { step: '02', role: 'PM (Claude)', text: 'Turns decisions into scoped specs and acceptance criteria.' },
  {
    step: '03',
    role: 'Engineers (Claude Code, Cursor)',
    text: 'Build, test and open PRs. Nothing merges without my review.',
  },
];

const CAPABILITIES = [
  {
    label: 'AI & Prompting',
    items: 'Prompt engineering · Context engineering · Directing AI coding agents · Cursor · Claude Code · ChatGPT · Perplexity',
  },
  { label: 'Product', items: 'Product strategy · Architecture decisions · Release management · Live testing & triage' },
  { label: 'Full-stack', items: 'React · Vite · Node.js · Vercel serverless · REST APIs · Server-rendered HTML' },
  {
    label: 'Money & Data',
    items: 'Stripe Checkout & Connect · Supabase / PostgreSQL · Row-level security · Klaviyo',
  },
  {
    label: 'Markets',
    items: 'Tastytrade & Tradier APIs · Options mechanics · Risk-engine design · Broker reconciliation',
  },
  { label: 'Automation', items: 'Playwright · Selenium · Webhooks · Idempotent writes · Performance diagnosis' },
];

const CREDIBILITY = ['Founder & CEO, Seamlessly', 'Author, Chaos Mastery', 'Keynote speaker on AI automation'];

const TickerItems = ({ hidden = false }) => (
  <ul className="ticker-group" aria-hidden={hidden || undefined}>
    {TICKER.map((item) => (
      <li key={item.text} className="ticker-item">
        <span className={`status-light tone-${item.tone}`} aria-hidden="true">
          {item.light}
        </span>
        {item.text}
      </li>
    ))}
  </ul>
);

const Home = () => {
  usePageMeta(null);

  return (
    <div className="home">
      {/* 1. Hero */}
      <section className="hero container" aria-labelledby="hero-heading">
        <p className="label hero-eyebrow">Maurice Sanders · Founder · Product builder · AI operator</p>
        <h1 id="hero-heading" className="hero-headline">
          I build AI solutions that solve real problems, increase productivity, for people that want to{' '}
          <em>move the needle on company deliverables.</em>
          {/* ALT HEADLINES — CEO may swap:
              "Problems solved. Productivity multiplied. Needle moved."
              "I turn AI into results: problems solved, hours saved, revenue earned." */}
        </h1>
        <div className="hero-body">
          <p className="hero-lede">
            The proof: a production 0DTE options trading platform, a live ticketing platform that earns a fee on every
            ticket sold, and an AI concierge that plans a whole night out. I write the specs, make the architecture
            calls, and direct AI coding agents from first prompt to production, so a team of one ships like a team of
            ten.
          </p>
          <ul className="hero-roles" aria-label="Roles">
            {ROLES.map((role) => (
              <li key={role}>{role}</li>
            ))}
          </ul>
          <div className="button-row">
            <a href="#flagships" className="primary-button">
              See the work
            </a>
            <a href={RESUME_PATH} download={RESUME_DOWNLOAD_NAME} className="secondary-button">
              Download resume
            </a>
          </div>
        </div>
      </section>

      {/* 2. Proof ticker */}
      <section className="ticker" aria-label="Proof">
        <div className="ticker-track">
          <TickerItems />
          <TickerItems hidden />
        </div>
      </section>

      {/* 3–4. Flagships */}
      <div id="flagships" className="container">
        {flagships.map((study, i) => (
          <Flagship key={study.slug} study={study} reversed={i % 2 === 1} />
        ))}
      </div>

      {/* 5. Also building */}
      <div className="container">
      <section className="home-band" aria-labelledby="also-building">
        <h2 id="also-building" className="label label-accent">
          Also building
        </h2>
        {alsoBuilding.map((study) => (
          <div key={study.slug} className="also-row">
            <div className="also-head">
              <h3 className="also-title">{study.alsoBuilding.title}</h3>
              <StatusBadge status={study.alsoBuilding.badge} />
            </div>
            <p className="also-line">{study.alsoBuilding.line}</p>
            <Link to={`/projects/${study.slug}`} className="text-link">
              Read the case study <span className="arrow" aria-hidden="true">→</span>
            </Link>
          </div>
        ))}
      </section>
      </div>

      {/* 6. How I ship */}
      <div className="container">
      <section className="home-band" aria-labelledby="how-i-ship">
        <h2 id="how-i-ship" className="label label-accent">
          How I ship
        </h2>
        <ol className="ship-steps">
          {HOW_I_SHIP.map((item) => (
            <li key={item.step}>
              <span className="ship-index">
                {item.step} · {item.role}
              </span>
              <p>{item.text}</p>
            </li>
          ))}
        </ol>
        <p className="label ship-footnote">Prompt engineering · Context engineering · Directing AI coding agents</p>
      </section>
      </div>

      {/* 7. Capabilities */}
      <div className="container">
      <section className="home-band" aria-labelledby="capabilities">
        <h2 id="capabilities" className="label label-accent">
          Capabilities
        </h2>
        <dl className="capabilities">
          {CAPABILITIES.map((cap) => (
            <div key={cap.label} className="capability">
              <dt className="label label-accent">{cap.label}</dt>
              <dd>{cap.items}</dd>
            </div>
          ))}
        </dl>
      </section>
      </div>

      {/* 8. Credibility strip */}
      <section className="credibility" aria-label="Credentials">
        <ul className="credibility-list container">
          {CREDIBILITY.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      {/* 9. Closing CTA */}
      <section className="container home-cta" aria-labelledby="closing-cta">
        <h2 id="closing-cta" className="home-cta-heading">
          Have a problem worth solving? <em>Let's move the needle.</em>
        </h2>
        <div className="button-row">
          <Link to="/contact" className="primary-button">
            Get in touch
          </Link>
          <a href={RESUME_PATH} download={RESUME_DOWNLOAD_NAME} className="secondary-button">
            Download resume
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
